/**
 * NEXUS UI Component Catalog
 * Organized showcase of all available components
 */

import React, { useState, useEffect } from 'react';
import {
  HoloPanel,
  HoloCard,
  MetricCounter,
  ProgressArc,
  ProgressBar,
  Sparkline,
  RadarChart,
  AreaChart,
  BarChart,
  DonutChart,
  LineChart,
  Heatmap,
  Waveform,
  LogViewer,
  QueueVisualizer,
  StatusIndicator,
  AlertCallout,
  HexGrid,
  Gauge,
  RadarScan,
  DataGrid,
  Timeline,
  Loader,
  Toggle,
  RangeSlider,
  SegmentedControl,
  SearchInput,
} from './components/nexus';
import type { TimelineEvent, HexCell, RadarDataPoint } from './components/nexus';

import './styles/nexus-tokens.css';
import './styles/nexus-components.css';

// ============================================
// Sample Data
// ============================================

const sparklineData = [30, 45, 28, 50, 42, 65, 55, 70, 62, 78, 72, 85];

const areaChartData = [
  { label: 'Mon', value: 65 },
  { label: 'Tue', value: 78 },
  { label: 'Wed', value: 52 },
  { label: 'Thu', value: 91 },
  { label: 'Fri', value: 83 },
  { label: 'Sat', value: 56 },
  { label: 'Sun', value: 72 },
];

const barChartData = [
  { label: 'API', value: 85 },
  { label: 'Web', value: 65 },
  { label: 'Mobile', value: 45 },
  { label: 'Desktop', value: 72 },
];

const lineChartSeries = [
  {
    id: 'revenue',
    label: 'Revenue',
    color: 'cyan' as const,
    data: [
      { label: 'Jan', value: 65 },
      { label: 'Feb', value: 72 },
      { label: 'Mar', value: 58 },
      { label: 'Apr', value: 85 },
      { label: 'May', value: 78 },
      { label: 'Jun', value: 92 },
    ],
  },
  {
    id: 'costs',
    label: 'Costs',
    color: 'red' as const,
    data: [
      { label: 'Jan', value: 45 },
      { label: 'Feb', value: 52 },
      { label: 'Mar', value: 48 },
      { label: 'Apr', value: 62 },
      { label: 'May', value: 58 },
      { label: 'Jun', value: 72 },
    ],
  },
  {
    id: 'profit',
    label: 'Profit',
    color: 'green' as const,
    data: [
      { label: 'Jan', value: 80 },
      { label: 'Feb', value: 75 },
      { label: 'Mar', value: 82 },
      { label: 'Apr', value: 78 },
      { label: 'May', value: 88 },
      { label: 'Jun', value: 85 },
    ],
  },
];

const donutData = [
  { label: 'Active', value: 45, color: 'green' as const },
  { label: 'Pending', value: 25, color: 'amber' as const },
  { label: 'Inactive', value: 20, color: 'blue' as const },
  { label: 'Error', value: 10, color: 'red' as const },
];

const radarData: RadarDataPoint[] = [
  { label: 'CPU', value: 72 },
  { label: 'Memory', value: 58 },
  { label: 'Network', value: 85 },
  { label: 'Storage', value: 43 },
  { label: 'GPU', value: 67 },
];

const heatmapData = Array.from({ length: 7 }, (_, y) =>
  Array.from({ length: 12 }, (_, x) => ({
    x: x,
    y: y,
    value: Math.round(Math.random() * 100),
  }))
).flat();

const timelineEvents: TimelineEvent[] = [
  { id: '1', time: '09:00', title: 'System Boot', description: 'All services initialized', status: 'success' },
  { id: '2', time: '09:15', title: 'Database Sync', description: 'Replication completed', status: 'success' },
  { id: '3', time: '09:30', title: 'High CPU Alert', description: 'Threshold exceeded', status: 'warning', active: true },
  { id: '4', time: '10:00', title: 'Scheduled Backup', description: 'Pending execution', status: 'info' },
];

const hexCells: HexCell[] = Array.from({ length: 18 }, (_, i) => ({
  id: `cell-${i}`,
  status: i === 5 ? 'critical' : i === 8 ? 'warning' : i % 3 === 0 ? 'active' : 'inactive',
  label: `N${i + 1}`,
}));

const radarBlips = [
  { id: '1', x: 30, y: -40, label: 'Alpha', status: 'active' as const },
  { id: '2', x: -50, y: 20, label: 'Beta', status: 'warning' as const },
  { id: '3', x: 60, y: 50, label: 'Gamma', status: 'critical' as const },
];

const logEntries = [
  { id: '1', timestamp: '10:23:45', level: 'info' as const, message: 'Connection established to primary database' },
  { id: '2', timestamp: '10:23:46', level: 'success' as const, message: 'Authentication successful for user admin' },
  { id: '3', timestamp: '10:23:47', level: 'warning' as const, message: 'High memory usage detected: 87%' },
  { id: '4', timestamp: '10:23:48', level: 'error' as const, message: 'Failed to connect to backup server' },
  { id: '5', timestamp: '10:23:49', level: 'info' as const, message: 'Retry attempt 1 of 3...' },
];

const queueJobs = [
  { id: '1', name: 'Build #1247', status: 'completed' as const, progress: 100 },
  { id: '2', name: 'Build #1248', status: 'processing' as const, progress: 67 },
  { id: '3', name: 'Build #1249', status: 'pending' as const, progress: 0 },
  { id: '4', name: 'Build #1250', status: 'pending' as const, progress: 0 },
];

// ============================================
// Component Section Wrapper
// ============================================

const ComponentSection: React.FC<{
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <div style={{
    background: 'var(--nx-glass-dark)',
    border: '1px solid rgba(0, 212, 255, 0.1)',
    borderRadius: '8px',
    padding: '20px',
  }}>
    <h4 style={{
      fontFamily: 'var(--nx-font-interface)',
      fontSize: '13px',
      fontWeight: 600,
      color: 'var(--nx-text-primary)',
      marginBottom: '4px',
    }}>
      {title}
    </h4>
    <p style={{
      fontFamily: 'var(--nx-font-interface)',
      fontSize: '11px',
      color: 'var(--nx-text-tertiary)',
      marginBottom: '12px',
    }}>
      {description}
    </p>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-start' }}>
      {children}
    </div>
  </div>
);

const CategorySection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <section style={{ marginBottom: '48px' }}>
    <h3 style={{
      fontFamily: 'var(--nx-font-display)',
      fontSize: '18px',
      fontWeight: 600,
      color: 'var(--nx-cyan-base)',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      marginBottom: '20px',
      paddingBottom: '8px',
      borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
    }}>
      {title}
    </h3>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: '16px',
    }}>
      {children}
    </div>
  </section>
);

// ============================================
// Main Demo Component
// ============================================

export const NexusDemo: React.FC = () => {
  // State for interactive components
  const [toggleState, setToggleState] = useState(true);
  const [sliderValue, setSliderValue] = useState(65);
  const [segmentValue, setSegmentValue] = useState('day');
  const [searchValue, setSearchValue] = useState('');

  // Animated values for live demos
  const [liveValue, setLiveValue] = useState(72);
  const [waveformData, setWaveformData] = useState<number[]>([]);

  useEffect(() => {
    // Update live value periodically
    const interval = setInterval(() => {
      setLiveValue(prev => Math.max(0, Math.min(100, prev + (Math.random() - 0.5) * 10)));
    }, 2000);

    // Generate waveform data
    const waveInterval = setInterval(() => {
      setWaveformData(Array.from({ length: 50 }, (_, i) =>
        Math.sin(i * 0.3 + Date.now() * 0.002) * 0.5 + Math.random() * 0.3
      ));
    }, 100);

    return () => {
      clearInterval(interval);
      clearInterval(waveInterval);
    };
  }, []);

  const searchSuggestions = searchValue ? [
    { id: '1', label: 'Server Status', description: 'View server metrics' },
    { id: '2', label: 'Security Alerts', description: 'Recent events' },
  ].filter(s => s.label.toLowerCase().includes(searchValue.toLowerCase())) : [];

  return (
    <div className="nexus-ui" style={{
      minHeight: '100vh',
      background: 'var(--nx-void-deep)',
      padding: '48px 24px',
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <header style={{ textAlign: 'center', marginBottom: '64px' }}>
          <h1 style={{
            fontFamily: 'var(--nx-font-display)',
            fontSize: '48px',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #00d4ff 0%, #a855f7 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '16px',
          }}>
            NEXUS UI
          </h1>
          <p style={{
            fontFamily: 'var(--nx-font-interface)',
            fontSize: '16px',
            color: 'var(--nx-text-secondary)',
          }}>
            Futuristic Dashboard Component Library
          </p>
        </header>

        {/* ============================================
            CHARTS & DATA VISUALIZATION
            ============================================ */}
        <CategorySection title="Charts & Data Visualization">
          <ComponentSection
            title="Area Chart"
            description="Filled line chart for showing trends over time with visual emphasis on volume"
          >
            <AreaChart data={areaChartData} variant="cyan" width={280} height={160} showGrid showLabels fill />
          </ComponentSection>

          <ComponentSection
            title="Line Chart"
            description="Multi-series line chart for comparing trends across different data sets"
          >
            <LineChart
              series={lineChartSeries}
              width={280}
              height={160}
              showGrid
              showLegend
            />
          </ComponentSection>

          <ComponentSection
            title="Bar Chart"
            description="Vertical bar chart for comparing discrete categories"
          >
            <BarChart data={barChartData} variant="cyan" width={260} height={160} showLabels showValues />
          </ComponentSection>

          <ComponentSection
            title="Donut Chart"
            description="Circular chart for showing proportions of a whole"
          >
            <DonutChart data={donutData} size={160} showLegend centerLabel="Status" centerValue={100} />
          </ComponentSection>

          <ComponentSection
            title="Radar Chart"
            description="Multi-axis chart for displaying multivariate data"
          >
            <RadarChart data={radarData} variant="cyan" size={220} showLabels showValues />
          </ComponentSection>

          <ComponentSection
            title="Sparkline"
            description="Compact inline trend indicator for embedding in text or tables"
          >
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Sparkline data={sparklineData} variant="cyan" width={80} height={32} />
              <Sparkline data={sparklineData} variant="green" width={80} height={32} />
              <Sparkline data={sparklineData} variant="red" width={80} height={32} />
            </div>
          </ComponentSection>

          <ComponentSection
            title="Heatmap"
            description="Grid-based visualization for showing intensity patterns across two dimensions"
          >
            <Heatmap
              data={heatmapData}
              variant="cyan"
              cellSize={16}
              xLabels={['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']}
              yLabels={['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']}
            />
          </ComponentSection>

          <ComponentSection
            title="Waveform"
            description="Real-time oscilloscope-style display for audio, signals, or live data streams"
          >
            <Waveform data={waveformData} variant="green" width={280} height={80} showGrid />
          </ComponentSection>
        </CategorySection>

        {/* ============================================
            PROGRESS INDICATORS
            ============================================ */}
        <CategorySection title="Progress Indicators">
          <ComponentSection
            title="Progress Arc"
            description="Circular progress ring for showing completion percentage"
          >
            <div style={{ display: 'flex', gap: '24px' }}>
              <ProgressArc value={72} variant="cyan" size="md" label="CPU" />
              <ProgressArc value={45} variant="green" size="md" label="Memory" />
              <ProgressArc value={88} variant="amber" size="md" label="Disk" />
            </div>
          </ComponentSection>

          <ComponentSection
            title="Progress Bar"
            description="Linear progress indicator with optional segments"
          >
            <div style={{ width: '100%', maxWidth: '280px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <ProgressBar value={65} variant="cyan" size="md" label="Download" showValue />
              <ProgressBar value={45} variant="green" size="md" segments={10} label="Upload" showValue />
              <ProgressBar value={88} variant="amber" size="sm" />
            </div>
          </ComponentSection>

          <ComponentSection
            title="Gauge"
            description="Semi-circular gauge with needle for showing values within a range"
          >
            <div style={{ display: 'flex', gap: '24px' }}>
              <Gauge value={liveValue} label="CPU" unit="%" variant="cyan" size="sm" />
              <Gauge value={72} label="Temp" unit="°C" variant="amber" size="sm" warningThreshold={70} criticalThreshold={85} />
              <Gauge value={45} label="Load" unit="%" variant="blue" size="sm" />
            </div>
          </ComponentSection>
        </CategorySection>

        {/* ============================================
            METRICS & COUNTERS
            ============================================ */}
        <CategorySection title="Metrics & Counters">
          <ComponentSection
            title="Metric Counter"
            description="Animated number display for key statistics with optional trend indicator"
          >
            <div style={{ display: 'flex', gap: '24px' }}>
              <HoloCard label="Active Users" variant="cyan">
                <MetricCounter value={1247} variant="cyan" size="lg" glow />
              </HoloCard>
              <HoloCard label="Revenue" variant="green">
                <MetricCounter
                  value={84520}
                  prefix="$"
                  variant="green"
                  size="lg"
                  glow
                  delta={{ value: 12.5, type: 'positive' }}
                />
              </HoloCard>
              <HoloCard label="Error Rate" variant="red">
                <MetricCounter
                  value={2.4}
                  unit="%"
                  decimals={1}
                  variant="red"
                  size="lg"
                  glow
                  delta={{ value: 0.3, type: 'negative' }}
                />
              </HoloCard>
            </div>
          </ComponentSection>
        </CategorySection>

        {/* ============================================
            STATUS & ALERTS
            ============================================ */}
        <CategorySection title="Status & Alerts">
          <ComponentSection
            title="Status Indicator"
            description="Visual status dots for showing system or item states"
          >
            <div style={{ display: 'flex', gap: '24px' }}>
              <StatusIndicator status="active" label="Online" />
              <StatusIndicator status="warning" label="Degraded" />
              <StatusIndicator status="critical" label="Offline" />
              <StatusIndicator status="processing" label="Syncing" />
              <StatusIndicator status="inactive" label="Standby" />
            </div>
          </ComponentSection>

          <ComponentSection
            title="Alert Callout"
            description="Attention-grabbing notification banners for different severity levels"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
              <AlertCallout type="info" title="System Update" message="New version available" />
              <AlertCallout type="success" title="Backup Complete" message="All data synchronized" />
              <AlertCallout type="warning" title="High Memory" message="Usage exceeds 80%" />
              <AlertCallout type="critical" title="Connection Lost" message="Attempting to reconnect" />
            </div>
          </ComponentSection>
        </CategorySection>

        {/* ============================================
            DATA DISPLAY
            ============================================ */}
        <CategorySection title="Data Display">
          <ComponentSection
            title="Data Grid"
            description="Tabular data display with sorting and formatting options"
          >
            <DataGrid
              data={[
                { id: 1, name: 'web-server-01', status: 'active', cpu: 45, memory: 62 },
                { id: 2, name: 'web-server-02', status: 'active', cpu: 78, memory: 55 },
                { id: 3, name: 'db-primary', status: 'warning', cpu: 92, memory: 88 },
                { id: 4, name: 'cache-01', status: 'active', cpu: 23, memory: 41 },
              ]}
              columns={[
                { key: 'name', header: 'Server', width: '140px' },
                { key: 'status', header: 'Status', render: (v) => (
                  <StatusIndicator status={v as 'active' | 'warning'} size="sm" />
                )},
                { key: 'cpu', header: 'CPU %', mono: true },
                { key: 'memory', header: 'MEM %', mono: true },
              ]}
              variant="cyan"
            />
          </ComponentSection>

          <ComponentSection
            title="Timeline"
            description="Chronological event display for logs, history, or scheduled items"
          >
            <div style={{ width: '100%' }}>
              <Timeline events={timelineEvents} variant="cyan" />
            </div>
          </ComponentSection>

          <ComponentSection
            title="Log Viewer"
            description="Scrolling log display with severity-based coloring"
          >
            <LogViewer logs={logEntries} maxHeight={200} showTimestamp variant="cyan" />
          </ComponentSection>

          <ComponentSection
            title="Queue Visualizer"
            description="Visual representation of queued items with progress"
          >
            <QueueVisualizer jobs={queueJobs} variant="cyan" />
          </ComponentSection>
        </CategorySection>

        {/* ============================================
            SPECIALIZED DISPLAYS
            ============================================ */}
        <CategorySection title="Specialized Displays">
          <ComponentSection
            title="Radar Scan"
            description="Animated radar display with blip markers for spatial data"
          >
            <RadarScan blips={radarBlips} size={200} variant="cyan" sweepEnabled showLabels />
          </ComponentSection>

          <ComponentSection
            title="Hex Grid"
            description="Honeycomb-style grid for node or status visualization"
          >
            <HexGrid cells={hexCells} columns={6} cellSize={36} />
          </ComponentSection>
        </CategorySection>

        {/* ============================================
            INTERACTIVE CONTROLS
            ============================================ */}
        <CategorySection title="Interactive Controls">
          <ComponentSection
            title="Toggle"
            description="On/off switch for binary settings"
          >
            <div style={{ display: 'flex', gap: '24px' }}>
              <Toggle checked={toggleState} onChange={setToggleState} label="Enabled" variant="cyan" />
              <Toggle checked={true} onChange={() => {}} label="Active" variant="green" />
              <Toggle checked={false} onChange={() => {}} label="Disabled" variant="red" />
            </div>
          </ComponentSection>

          <ComponentSection
            title="Range Slider"
            description="Slider control for selecting values within a range"
          >
            <div style={{ width: '300px' }}>
              <RangeSlider
                value={sliderValue}
                onChange={setSliderValue}
                min={0}
                max={100}
                label="Threshold"
                showValue
                variant="cyan"
              />
            </div>
          </ComponentSection>

          <ComponentSection
            title="Segmented Control"
            description="Tab-style selector for switching between options"
          >
            <SegmentedControl
              value={segmentValue}
              onChange={setSegmentValue}
              segments={[
                { value: 'hour', label: '1H' },
                { value: 'day', label: '24H' },
                { value: 'week', label: '7D' },
                { value: 'month', label: '30D' },
              ]}
              variant="cyan"
            />
          </ComponentSection>

          <ComponentSection
            title="Search Input"
            description="Search field with autocomplete suggestions"
          >
            <div style={{ width: '300px' }}>
              <SearchInput
                value={searchValue}
                onChange={setSearchValue}
                placeholder="Search..."
                suggestions={searchSuggestions}
                onSuggestionSelect={(s) => setSearchValue(s.label)}
                variant="cyan"
              />
            </div>
          </ComponentSection>
        </CategorySection>

        {/* ============================================
            CONTAINERS
            ============================================ */}
        <CategorySection title="Containers">
          <ComponentSection
            title="Holo Panel"
            description="Glass-morphism container with angular cuts for grouping content"
          >
            <HoloPanel variant="cyan" style={{ padding: '24px', width: '300px' }}>
              <p style={{ color: 'var(--nx-text-secondary)', margin: 0 }}>
                Content inside a HoloPanel with holographic glass effect and edge glow.
              </p>
            </HoloPanel>
          </ComponentSection>

          <ComponentSection
            title="Holo Card"
            description="Metric display card with accent border and hover effects"
          >
            <div style={{ display: 'flex', gap: '16px' }}>
              <HoloCard label="Card Title" variant="cyan">
                <p style={{ color: 'var(--nx-text-secondary)', margin: 0 }}>Card content</p>
              </HoloCard>
              <HoloCard label="Another Card" variant="violet">
                <p style={{ color: 'var(--nx-text-secondary)', margin: 0 }}>More content</p>
              </HoloCard>
            </div>
          </ComponentSection>
        </CategorySection>

        {/* ============================================
            LOADERS
            ============================================ */}
        <CategorySection title="Loaders">
          <ComponentSection
            title="Loading Spinners"
            description="Animated loading indicators in various styles"
          >
            <div style={{ display: 'flex', gap: '48px', alignItems: 'center' }}>
              <Loader type="ring" size="md" variant="cyan" />
              <Loader type="hexagon" size="md" variant="cyan" />
              <Loader type="pulse" size="md" variant="cyan" />
              <Loader type="orbit" size="md" variant="cyan" />
            </div>
          </ComponentSection>
        </CategorySection>

        {/* Footer */}
        <footer style={{
          textAlign: 'center',
          marginTop: '64px',
          paddingTop: '24px',
          borderTop: '1px solid rgba(0, 212, 255, 0.1)',
        }}>
          <p style={{
            fontFamily: 'var(--nx-font-interface)',
            fontSize: '12px',
            color: 'var(--nx-text-ghost)',
          }}>
            NEXUS UI Component Library
          </p>
        </footer>
      </div>
    </div>
  );
};

export default NexusDemo;
